import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import {
    DEFAULT_AXNUI_CONTENT,
    DEFAULT_AXNUI_LAYOUT,
    DEFAULT_AXNUI_THEME,
    DEFAULT_DCUI_CONTENT,
    DEFAULT_DCUI_THEME
} from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import {
    AXNUI_CONTENT_PROPERTY_TYPE_ID,
    AXNUI_LAYOUT_PROPERTY_TYPE_ID,
    AXNUI_THEME_PROPERTY_TYPE_ID,
    DCUI_VERSION_PROPERTY_TYPE_ID,
    USE_THEMED_UI_PROPERTY_TYPE_ID,
    DCUI_THEME_PROPERTY_TYPE_ID,
    DCUI_CONTENT_PROPERTY_TYPE_ID,
    INCODE_BIOGOVID_VERIFICATION_SERVICE_OFFERING_ID,
    LEXISNEXIS_KBA_SERVICE_OFFERING_ID,
    EMAIL_FROM_ADDRESS_PROPERTY_TYPE_ID,
    REDIRECT_URLS_PROPERTY_TYPE_ID,
    INCODE_BIOGOVID_DUAL_DOCUMENT_VERIFICATION_SERVICE_OFFERING_ID,
    SELFIE_DATABASE_MATCH_INCODE_SERVICE_OFFERING_ID,
    SELFIE_DATABASE_MATCH_PLUS_DEEPFAKE_DETECTION_INCODE_SERVICE_OFFERING_ID,
    INCODE_BIOGOVID_VERIFICATION_DEEPFAKE_SERVICE_OFFERING_ID,
    BIOGOVID_VERIFICATION_COMBO_SERVICE_OFFERING_ID
} from "@constants/verification-service-details-constants";
import { resources } from "@constants/hardcoded-strings";
import { DISPLAY_RESULTS_PROPERTY_TYPE_ID } from "@constants/landing-page-constants";
import { OIDC_LOGIN_SERVICE_TYPE } from "@constants/verification-service-types";
import { SERVER_TIME_FORMAT } from "@constants/date-formats";
import { SpinnerModal } from "@containers/common/modals/spinner-modal/spinner-modal";
import { useModalContext } from "@context/use-modal-context";
import { AppliedProfileTypes } from "@enums/applied-profile-types";
import { ModalTypes } from "@enums/modal-types";
import { ServiceEndpointTypes } from "@enums/service-endpoint-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { AttributeClassificationTypes } from "@enums/attribute-classification-types";
import { PolicyTypes } from "@enums/policy-types";
import { MessagingTemplateTypes } from "@enums/messaging-template-types";
import { formatAppliedProfiles } from "@formatters/applied-profiles/applied-profiles-formatter";
import { FormattedAppliedProfile } from "@idw-types/data/applied-profile/applied-profile";
import { ScoreReferenceData } from "@idw-types/data/rp-service-configuration/scores/score-reference-data";
import {
    AssertionAttribute,
    AttributeLabel,
    AvailableAssertionStatus,
    RpServiceAttributeProperty,
    ScoreAttributeMap,
    ServiceOfferingRefreshRate
} from "@idw-types/data/reference-data/generic";
import { FormattedRpServiceConfiguration, FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import {
    AxnAttributeType,
    AxnAttributeTypeField,
    AxnAttributeTypeFieldCustomization,
    AxnAvailablePropertyCategory,
    AxnCompany,
    AxnRelyingParty,
    SimpleAxnWorkflow
} from "@idw-types/data/reference-data/axn";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { VerificationServiceDetailsValidationErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { IDWSession } from "@idw-types/data/idw-session";
import {
    FormattedMessagingTemplate,
    MessagingTemplateConfiguration,
    MessagingTemplateCache
} from "@idw-types/data/messaging-template";
import { EmailTemplateFields } from "@idw-types/common/email-template-fields";
import { getAxnRpServicesRequest, getWorkflowDetailsAxnRequest } from "@requests/service-configuration";
import { getActiveDCUICaptureSteps, isAttributeDisplayable, isTmxInDCUIEnabled } from "@utils/dcui-capture-step-util";
import { getActiveAcuantProperties } from "@utils/dcui-data-util";
import { setPartialCommonReferenceData } from "@redux/actions/common-reference-data-actions";
import { uploadResourceRequest } from "@requests/service-configuration/upload-resource";
import { extractAvailablePropertiesFromCategories } from "@utils/available-properties-util";
import { logAndDisplayErrorModal } from "@utils/app-alert-util";
import { convertFileToBase64 } from "@utils/file-util";
import { getPolicyTypeById } from "@utils/policies/policy-util";
import { getVerificationServiceValidationErrors } from "@utils/validation/verification-service/verification-service-validation-util";
import {
    getInheritedAndMergedContentPropertyValue,
    getInheritedAndMergedJSONPropertyValue,
    getInheritedAndMergedStringPropertyValue
} from "@utils/verification-service-details-util";
import {
    getCombinedAppliedProfileRedirectURLs,
    getFormattedRpServiceConfigurationPropertyRedirectURLs,
    getRedirectUrlsInformation
} from "@utils/redirect-urls/redirect-urls-util";
import { sortNumbers } from "@utils/number-util";
import { hasPinServiceOffering } from "@utils/pin-code/pin-code-utils";
import { TabContainer } from "@containers/common/tab-container/tab-container";
import { VerificationServiceDetailsAttributesTabContainer } from "./verification-service-details-attributes-tab/verification-service-details-attributes-tab";
import { VerificationServiceDetailsThemingTab } from "./verification-service-details-theming-tab/verification-service-details-theming-tab";
import { VerificationServiceDetailsAssertionsTab } from "./verification-service-details-assertions-tab/verification-service-details-assertions-tab";
import { VerificationServiceDetailsGeneralTab } from "./verification-service-details-general-tab/verification-service-details-general-tab";
import { VerificationServiceDetailsServicesTab } from "./verification-service-details-services-tab/verification-service-details-services-tab";
import { VerificationServiceDetailsPoliciesTabContainer } from "./verification-service-details-policies-tab/verification-service-details-policies-tab";
import { VerificationServiceDetailsDCUIThemingTab } from "./verification-service-details-dcui-theming-tab/verification-service-details-dcui-theming-tab";
import { VerificationServiceDetailsFederatedIdpTab } from "./verification-service-details-federated-idp-tab/verification-service-details-federated-idp-tab";
import { VerificationServiceDetailsScoresTab } from "./verification-service-details-scores-tab/verification-service-details-scores-tab";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { BaseRedirectUrl } from "@idw-types/redirect-url-information";
import { RedirectUrlOriginTypes } from "@enums/redirect-url-origin-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { getCustomLanguages } from "@utils/language-selector-util";

import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";

type VerificationServiceDetailsTabContainerProps = {
    isDisabled: boolean;
    selectedLanguage: LocaleTypes;
    availableLanguages: Array<LocaleTypes>;
    setSelectedLanguage: (newLanguage: LocaleTypes) => void;
    setAvailableLanguages: (availableLanguages: Array<LocaleTypes>) => void;
    session: IDWSession;
    attributeLabels: Array<AttributeLabel>;
    assertionAttributes: Array<AssertionAttribute>;
    assertionStatusMaps: Array<ApAssertionStatusMap>;
    assertionScoreServiceOfferingIDs: Array<string>;
    trustScoreServiceOfferingIDs: Array<string>;
    scoreReferenceData: ScoreReferenceData;
    scoreAttributeMaps: Array<ScoreAttributeMap>;
    axnRpServices: Array<AxnRpService>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    serviceOfferingRefreshRates: Array<ServiceOfferingRefreshRate>;
    serviceOfferingRefreshRateDefault: string;
    serviceAttributeProperties: Array<RpServiceAttributeProperty>;
    initialRpServiceConfiguration: FormattedRpServiceConfiguration;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    simpleRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    simpleAxnWorkflows: Array<SimpleAxnWorkflow>;
    simpleAxnAdminWorkflows: Array<SimpleAxnWorkflow>;
    availablePropertyCategories: Array<AxnAvailablePropertyCategory>;
    axnCompanies: Array<AxnCompany>;
    axnDocumentationLinks: Array<AxnDocumentationLink>;
    axnAttributeTypes: Array<AxnAttributeType>;
    axnAcquiredAttributes: Array<AxnAcquiredAttribute>;
    messagingTemplateCache: MessagingTemplateCache;
    messagingTemplateConfigurations: Array<MessagingTemplateConfiguration>;
    availableMessagingTemplateTypes: Array<MessagingTemplateTypes>;
    inheritedEmailTemplateFields: EmailTemplateFields;
    mergedEmailTemplateFields: EmailTemplateFields;
    selectedMessagingTemplates: Array<FormattedMessagingTemplate>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
    hasUnsubmittedChangeRequest: boolean;
    requestInProgress: boolean;
    setHasFetchedMessagingTemplates: (messagingTemplateCache: MessagingTemplateCache) => void;
    setMessagingTemplateCache: (messagingTemplateCache: MessagingTemplateCache) => void;
    setRpServiceConfigurationState: (rpServiceConfiguration: FormattedRpServiceConfiguration) => void;
    generateHeaderComponent: (
        selectedMessagingTemplates: Array<FormattedMessagingTemplate>,
        mergedEmailTemplateFields: EmailTemplateFields,
        validationErrors: VerificationServiceDetailsValidationErrors
    ) => React.ReactNode;
};

export const VerificationServiceDetailsTabContainer = (props: VerificationServiceDetailsTabContainerProps) => {
    const {
        isDisabled,
        selectedLanguage,
        availableLanguages,
        setSelectedLanguage,
        setAvailableLanguages,
        session,
        attributeLabels,
        assertionAttributes,
        assertionStatusMaps,
        assertionScoreServiceOfferingIDs,
        trustScoreServiceOfferingIDs,
        axnRpServices,
        axnRelyingParties,
        axnAttributeTypeFields,
        serviceOfferingRefreshRates,
        serviceOfferingRefreshRateDefault,
        initialRpServiceConfiguration,
        rpServiceConfigurationState,
        scoreReferenceData,
        scoreAttributeMaps,
        serviceAttributeProperties,
        simpleRPServiceWorkflows,
        simpleAxnWorkflows,
        simpleAxnAdminWorkflows,
        axnCompanies,
        axnDocumentationLinks,
        availablePropertyCategories,
        axnAttributeTypes,
        axnAcquiredAttributes,
        availableMessagingTemplateTypes,
        inheritedEmailTemplateFields,
        mergedEmailTemplateFields,
        selectedMessagingTemplates,
        hasUnsubmittedChangeRequest,
        requestInProgress,
        axnAvailableAssertionStatuses,
        axnAttributeTypeFieldCustomizations,
        setRpServiceConfigurationState,
        generateHeaderComponent,
        messagingTemplateCache,
        setHasFetchedMessagingTemplates,
        setMessagingTemplateCache,
        messagingTemplateConfigurations
    } = props;

    const dispatch = useDispatch();
    const modalContext = useModalContext();
    const [isMultiLanguageEnabled, setIsMultiLanguageEnabled] = useState(false);

    const serviceProfileKey = `s${rpServiceConfigurationState.id}`;
    const initialPolicyType = useMemo(() => {
        const usePolicyManager = isStringTrue(initialRpServiceConfiguration.usePolicyManager);
        const policyType = getPolicyTypeById(
            usePolicyManager,
            initialRpServiceConfiguration.policyConfig,
            initialRpServiceConfiguration.policies
        );
        return policyType;
    }, [initialRpServiceConfiguration]);

    const {
        emailFromAddress,
    } = useMemo(() => {
        const {
            mergedValue,
            inheritedValue
        } = getInheritedAndMergedStringPropertyValue(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.appliedProfiles,
            EMAIL_FROM_ADDRESS_PROPERTY_TYPE_ID
        );

        return {
            emailFromAddress: mergedValue,
            inheritedEmailFromAddress: inheritedValue
        };
    }, [rpServiceConfigurationState.properties, rpServiceConfigurationState.appliedProfiles]);

    const isUsingThemedUI = useMemo(() => {
        const {
            mergedValue
        } = getInheritedAndMergedStringPropertyValue(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.appliedProfiles,
            USE_THEMED_UI_PROPERTY_TYPE_ID
        );

        return isStringTrue(mergedValue);
    }, [rpServiceConfigurationState.properties, rpServiceConfigurationState.appliedProfiles]);

    const isUsingDCUIVersion2 = useMemo(() => {
        const {
            mergedValue
        } = getInheritedAndMergedStringPropertyValue(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.appliedProfiles,
            DCUI_VERSION_PROPERTY_TYPE_ID
        );

        return mergedValue === "2.0";
    }, [rpServiceConfigurationState.properties, rpServiceConfigurationState.appliedProfiles]);

    const {
        isDisplayResultsEnabled,
        inheritedIsDisplayResultsEnabled
    } = useMemo(() => {
        const {
            mergedValue,
            inheritedValue
        } = getInheritedAndMergedStringPropertyValue(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.appliedProfiles,
            DISPLAY_RESULTS_PROPERTY_TYPE_ID
        );

        return {
            isDisplayResultsEnabled: isStringTrue(mergedValue),
            inheritedIsDisplayResultsEnabled: isStringTrue(inheritedValue)
        };
    }, [rpServiceConfigurationState.properties, rpServiceConfigurationState.appliedProfiles]);

    const hasDisplayableDocumentCaptureAttributes = useMemo(() => {
        const selectedAttributes = rpServiceConfigurationState.attributes
            .filter(attr => attr.addedToService && attr.status === EntityStatusTypes.ACTIVE);

        const isDisplayable = isAttributeDisplayable(selectedAttributes);

        const tmxInDCUIEnabled = isTmxInDCUIEnabled(rpServiceConfigurationState.properties);

        return isDisplayable || tmxInDCUIEnabled;
    }, [rpServiceConfigurationState.attributes, rpServiceConfigurationState.properties]);

    const hasActiveServiceOfferings = useMemo(() => {
        return !!rpServiceConfigurationState.serviceOfferings.find(so => so.addedToService && so.status === EntityStatusTypes.ACTIVE);
    }, [rpServiceConfigurationState.serviceOfferings]);

    const showMessagingTab = useMemo(() => {
        return availableMessagingTemplateTypes.length > 0;
    }, [availableMessagingTemplateTypes]);

    const showIncodeFlowTabs = useMemo(() => {
        return !!rpServiceConfigurationState.serviceOfferings.find(so =>
            so.addedToService &&
            so.status === EntityStatusTypes.ACTIVE &&
            [
                INCODE_BIOGOVID_VERIFICATION_SERVICE_OFFERING_ID,
                INCODE_BIOGOVID_DUAL_DOCUMENT_VERIFICATION_SERVICE_OFFERING_ID,
                SELFIE_DATABASE_MATCH_INCODE_SERVICE_OFFERING_ID,
                SELFIE_DATABASE_MATCH_PLUS_DEEPFAKE_DETECTION_INCODE_SERVICE_OFFERING_ID,
                INCODE_BIOGOVID_VERIFICATION_DEEPFAKE_SERVICE_OFFERING_ID,
                BIOGOVID_VERIFICATION_COMBO_SERVICE_OFFERING_ID
            ].includes(so.serviceOfferingID)
        );
    }, [rpServiceConfigurationState.serviceOfferings]);

    const showPinCodeSection = useMemo(() => {
        return hasPinServiceOffering(rpServiceConfigurationState.serviceOfferings);
    }, [rpServiceConfigurationState.serviceOfferings]);

    const showKbaFormTab = useMemo(() => {
        return !!rpServiceConfigurationState.serviceOfferings.find(so =>
            so.addedToService &&
            so.status === EntityStatusTypes.ACTIVE &&
            so.serviceOfferingID === LEXISNEXIS_KBA_SERVICE_OFFERING_ID
        );
    }, [rpServiceConfigurationState.serviceOfferings]);

    const policyType = useMemo(() => {
        const usePolicyManager = isStringTrue(rpServiceConfigurationState.usePolicyManager);
        return getPolicyTypeById(usePolicyManager, rpServiceConfigurationState.policyConfig, rpServiceConfigurationState.policies);
    }, [
        rpServiceConfigurationState.usePolicyManager,
        rpServiceConfigurationState.policyConfig?.status,
        rpServiceConfigurationState.policies
    ]);

    const isUIDisabled = useMemo(() => {
        return isDisabled || requestInProgress;
    }, [isDisabled, requestInProgress]);

    const {
        inheritedValue: inheritedAXNUITheme,
        mergedValue: mergedAXNUITheme
    } = useMemo(() => {
        return getInheritedAndMergedJSONPropertyValue(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.appliedProfiles,
            AXNUI_THEME_PROPERTY_TYPE_ID,
            DEFAULT_AXNUI_THEME
        )
    }, [rpServiceConfigurationState.properties, rpServiceConfigurationState.appliedProfiles]);

    const {
        inheritedValue: inheritedAXNUILayout,
        mergedValue: mergedAXNUILayout
    } = useMemo(() => {
        return getInheritedAndMergedJSONPropertyValue(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.appliedProfiles,
            AXNUI_LAYOUT_PROPERTY_TYPE_ID,
            DEFAULT_AXNUI_LAYOUT
        )
    }, [rpServiceConfigurationState.properties, rpServiceConfigurationState.appliedProfiles]);

    const {
        inheritedContent: inheritedAXNUIContent,
        mergedContent: mergedAXNUIContent,
        multiLanguageContent: multiLanguageAXNUIContent,
        availableLanguages: availableAXNUILanguages
    } = useMemo(() => {
        return getInheritedAndMergedContentPropertyValue(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.appliedProfiles,
            AXNUI_CONTENT_PROPERTY_TYPE_ID,
            DEFAULT_AXNUI_CONTENT,
            selectedLanguage
        );
    }, [
        rpServiceConfigurationState.properties,
        rpServiceConfigurationState.appliedProfiles,
        selectedLanguage
    ]);

    const {
        inheritedValue: inheritedDCUITheme,
        mergedValue: mergedDCUITheme
    } = useMemo(() => {
        return getInheritedAndMergedJSONPropertyValue(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.appliedProfiles,
            DCUI_THEME_PROPERTY_TYPE_ID,
            DEFAULT_DCUI_THEME
        )
    }, [rpServiceConfigurationState.properties, rpServiceConfigurationState.appliedProfiles]);

    const {
        inheritedContent: inheritedDCUIContent,
        mergedContent: mergedDCUIContent,
        multiLanguageContent: multiLanguageDCUIContent,
        availableLanguages: availableDCUILanguages
    } = useMemo(() => {
        return getInheritedAndMergedContentPropertyValue(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.appliedProfiles,
            DCUI_CONTENT_PROPERTY_TYPE_ID,
            DEFAULT_DCUI_CONTENT,
            selectedLanguage
        );
    }, [
        rpServiceConfigurationState.properties,
        rpServiceConfigurationState.appliedProfiles,
        selectedLanguage
    ]);

    const customLanguages: Array<LocaleTypes> = useMemo(() => {
        return getCustomLanguages(PageLevelTypes.APIKEY, [], rpServiceConfigurationState.properties);
    }, [rpServiceConfigurationState.properties]);

    const scoreServiceOfferingIDs = useMemo(() => {
        return scoreAttributeMaps.map(sam => sam.serviceOfferingID).filter((value, index, self) => self.indexOf(value) === index);
    }, [scoreAttributeMaps]);

    useEffect(() => {
        if (customLanguages.length === 0 && availableAXNUILanguages && availableDCUILanguages) {
            const combinedLanguages = Array.from(new Set([...availableAXNUILanguages, ...availableDCUILanguages]));

            if (combinedLanguages.length > 0) {
                if (!combinedLanguages.includes(selectedLanguage)) {
                    setSelectedLanguage(combinedLanguages[0]);
                }

                setAvailableLanguages(combinedLanguages);
                setIsMultiLanguageEnabled(true);
            } else if (selectedLanguage) {
                setSelectedLanguage(selectedLanguage);
                setAvailableLanguages([]);
            } else {
                setSelectedLanguage(LocaleTypes.ENGLISH);
                setAvailableLanguages([]);
            }
        } else if (customLanguages.length > 0) {
            setIsMultiLanguageEnabled(true);
            setAvailableLanguages(customLanguages);
        }
    }, [
        customLanguages,
        selectedLanguage,
        availableAXNUILanguages,
        availableDCUILanguages
    ]);

    const documentCaptureSteps = useMemo(() => {
        return getActiveDCUICaptureSteps(rpServiceConfigurationState.attributes);
    }, [rpServiceConfigurationState.attributes]);

    const activeAcuantProperties = useMemo(() => {
        return getActiveAcuantProperties(rpServiceConfigurationState.properties);
    }, [rpServiceConfigurationState.properties]);

    const rpServicePropertyRedirectURLs = useMemo(() => {
        return getFormattedRpServiceConfigurationPropertyRedirectURLs(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.id,
            true
        );
    }, [rpServiceConfigurationState.properties]);

    const profileLevelRedirectURLs = useMemo(() => {
        return getCombinedAppliedProfileRedirectURLs(rpServiceConfigurationState.appliedProfiles, false);
    }, [rpServiceConfigurationState.appliedProfiles]);

    const redirectUrls: Array<BaseRedirectUrl> = useMemo(() => {
        const isBelongToWorkflow = rpServiceConfigurationState.workflowID !== null;
        const redirectUrls: Array<BaseRedirectUrl> = [...rpServicePropertyRedirectURLs, ...profileLevelRedirectURLs];
        if (rpServiceConfigurationState.callBackURL) {
            redirectUrls.push({
                index: 0,
                url: rpServiceConfigurationState.callBackURL,
                isEditable: !isBelongToWorkflow,
                redirectUrlOrigin: RedirectUrlOriginTypes.CALLBACK_URL
            });
        }
        return [
            ...redirectUrls,
            ...rpServiceConfigurationState.redirects
                .map((redirect, index) => ({
                    index,
                    url: redirect.redirectURI,
                    isEditable: !isBelongToWorkflow,
                    redirectUrlOrigin: RedirectUrlOriginTypes.REDIRECT
                }))
        ];
    }, [
        rpServicePropertyRedirectURLs,
        profileLevelRedirectURLs,
        rpServiceConfigurationState.callBackURL,
        rpServiceConfigurationState.redirects,
        rpServiceConfigurationState.workflowID
    ]);

    const validationErrors = useMemo(() => {
        return getVerificationServiceValidationErrors(
            rpServiceConfigurationState,
            trustScoreServiceOfferingIDs,
            scoreServiceOfferingIDs,
            isUsingThemedUI,
            isUsingDCUIVersion2,
            hasDisplayableDocumentCaptureAttributes,
            showMessagingTab,
            showIncodeFlowTabs,
            showKbaFormTab,
            documentCaptureSteps,
            activeAcuantProperties,
            mergedAXNUIContent,
            mergedAXNUILayout,
            mergedAXNUITheme,
            mergedDCUIContent,
            mergedDCUITheme,
            isDisplayResultsEnabled,
            redirectUrls,
            availableMessagingTemplateTypes,
            selectedMessagingTemplates,
            mergedEmailTemplateFields,
            showPinCodeSection
        );
    }, [
        rpServiceConfigurationState,
        trustScoreServiceOfferingIDs,
        scoreServiceOfferingIDs,
        isUsingThemedUI,
        isUsingDCUIVersion2,
        hasDisplayableDocumentCaptureAttributes,
        showMessagingTab,
        showIncodeFlowTabs,
        showKbaFormTab,
        documentCaptureSteps,
        activeAcuantProperties,
        mergedAXNUIContent,
        mergedAXNUILayout,
        mergedAXNUITheme,
        mergedDCUIContent,
        mergedDCUITheme,
        isDisplayResultsEnabled,
        redirectUrls,
        rpServiceConfigurationState.workflowID,
        messagingTemplateCache,
        availableMessagingTemplateTypes,
        selectedMessagingTemplates,
        mergedEmailTemplateFields,
        showPinCodeSection
    ]);

    const handleGetWorkflowAppliedProfilesRequest = async (workflowID: string) => {
        if (workflowID !== null) {
            openSpinnerModal("Fetching Workflow Data");

            const { data, error } = await getWorkflowDetailsAxnRequest(workflowID);

            let workflowAppliedProfiles: Array<FormattedAppliedProfile> = [];
            // if axn workflow exists, extract and format applied profiles
            if (data) {
                const availableProperties = extractAvailablePropertiesFromCategories(availablePropertyCategories);
                workflowAppliedProfiles = formatAppliedProfiles(data.workflow.appliedProfiles, axnCompanies, availableProperties, false);
            } else if (error) {
                //if no details come back - this data is bad OR workflow was created by service and is only in axn admin
                console.error(`Error requesting workflow details for workflow ID: ${workflowID}, error: ${error}`);
            }

            const serviceAppliedProfiles = rpServiceConfigurationState.appliedProfiles.filter(ap => ap.type === AppliedProfileTypes.SERVICE);
            const mergedAppliedProfiles = [...workflowAppliedProfiles, ...serviceAppliedProfiles];
            // merge together workflow applied profiles and service applied profiles
            setRpServiceConfigurationState({
                ...rpServiceConfigurationState,
                workflowID,
                appliedProfiles: mergedAppliedProfiles
            });

            modalContext.setModalState({
                modalType: undefined,
                renderFunction: undefined
            });
        } else {
            const nonWorkflowAppliedProfiles = rpServiceConfigurationState.appliedProfiles.filter(ap => ap.type !== AppliedProfileTypes.WORKFLOW);

            setRpServiceConfigurationState({
                ...rpServiceConfigurationState,
                workflowID: null,
                appliedProfiles: nonWorkflowAppliedProfiles
            });
        }
    };

    const handleGetRelyingPartyServicesRequest = async (relyingPartyID: string) => {
        if (relyingPartyID) {
            openSpinnerModal("Fetching Relying Party Services");

            const { data, error } = await getAxnRpServicesRequest(ServiceEndpointTypes.OIDC, relyingPartyID);

            if (data?.axnRPServices) {
                dispatch(setPartialCommonReferenceData({ axnRPServices: data.axnRPServices }, null));
            } else if (error) {
                console.error(`Error requesting rpaxnservices for relying party ID: ${relyingPartyID}, error: ${error}`);
            }

            const relyingParty = axnRelyingParties.find(rp => rp.id === relyingPartyID);
            const organization = axnCompanies.find(rp => rp.id === relyingParty.companyID);

            setRpServiceConfigurationState({
                ...rpServiceConfigurationState,
                relyingParty: relyingParty,
                organization: organization
            });

            modalContext.setModalState({
                modalType: undefined,
                renderFunction: undefined
            });
        }
    };

    const onCreateWorkflow = (workflowName: string) => {
        const nonWorkflowAppliedProfiles = rpServiceConfigurationState.appliedProfiles.filter(ap => ap.type !== AppliedProfileTypes.WORKFLOW);

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            workflow: workflowName ? {
                name: workflowName
            } : null,
            workflowID: null,
            appliedProfiles: nonWorkflowAppliedProfiles
        });
    };

    const openSpinnerModal = (message: string) => {
        const renderSpinnerModal = (onModalClose: () => void) => {
            return (
                <SpinnerModal
                    onModalClose={onModalClose}
                    message={message}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.AddVerificationService,
            renderFunction: renderSpinnerModal
        });
    }

    const uploadFileToS3 = async (file: File, category: string): Promise<string> => {
        const serviceId = rpServiceConfigurationState.id;
        const fileTypeStringArray = file.type.split("/");
        const fileType = fileTypeStringArray[fileTypeStringArray.length - 1];
        const formattedDate = format(new Date(), SERVER_TIME_FORMAT);
        const fileName = `${category}-s${serviceId}-${formattedDate}`;
        const base64Data = await convertFileToBase64(file);

        if (base64Data) {
            const { data, error } = await uploadResourceRequest(base64Data, fileType, fileName, null, serviceId, category);
            if (data?.url) {
                return data.url;
            } else {
                if (error) {
                    logAndDisplayErrorModal(
                        dispatch,
                        error,
                        resources.requests.uploadResource.error.title,
                        resources.requests.uploadResource.error.description,
                    );
                }
            }
        }
        return null;
    };

    const updateProperties = (properties: Array<{ propertyTypeId: string, newValue: string, added: boolean }>) => {
        const newProperties: Array<FormattedRpServiceConfigurationProperty> = rpServiceConfigurationState.properties.map(property => {
            if (property.propertyTypeId === REDIRECT_URLS_PROPERTY_TYPE_ID) {
                if (property.addedToService && property.value) {
                    try {
                        const existingValue: { [key: string]: Array<string> } = JSON.parse(property.value);

                        const foundIndex = properties.findIndex(passedProperty => passedProperty.propertyTypeId === property?.propertyTypeId);
                        if (foundIndex > -1) {
                            if (existingValue[serviceProfileKey]) {
                                existingValue[serviceProfileKey][foundIndex] = properties[foundIndex].newValue;
                            }
                        }

                        return {
                            ...property,
                            value: JSON.stringify(existingValue)
                        };
                    } catch (error) {
                        console.error(error);
                    }
                }
            } else {
                const foundIndex = properties.findIndex(passedProperty => passedProperty.propertyTypeId === property?.propertyTypeId);
                if (foundIndex > -1) {
                    return {
                        ...property,
                        value: properties[foundIndex].newValue,
                        addedToService: properties[foundIndex].added
                    }
                }

            }
            return property;
        });

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties
        });
    };

    const headerComponent = useMemo(() => {
        if (generateHeaderComponent) {
            return generateHeaderComponent(
                selectedMessagingTemplates,
                mergedEmailTemplateFields,
                validationErrors
            );
        }
        return null;
    }, [
        generateHeaderComponent,
        selectedMessagingTemplates,
        mergedEmailTemplateFields,
        validationErrors
    ]);


    const filteredAttributeIds = useMemo(() => {
        return rpServiceConfigurationState.attributes
            .filter(attr => [
                AttributeClassificationTypes.PII,
                AttributeClassificationTypes.DEVICES,
                AttributeClassificationTypes.PREFERENCES,
                AttributeClassificationTypes.ORGANIZATION,
                AttributeClassificationTypes.OTHER,
                AttributeClassificationTypes.GOVERNMENT_ISSUED,
                AttributeClassificationTypes.CUSTOM
            ].includes(attr.attributeClassification?.name))
            .map(attr => attr.attributeType?.id);
    }, [rpServiceConfigurationState.attributes]);

    const selectedAttributes = rpServiceConfigurationState.attributes
        .filter(attribute => filteredAttributeIds.includes(attribute.attributeType?.id) && attribute.addedToService && attribute.status === EntityStatusTypes.ACTIVE)
        .sort((a, b) => sortNumbers(a.order, b.order));

    const redirectUrlsInformation = useMemo(() => {
        return getRedirectUrlsInformation(redirectUrls, true);
    }, [redirectUrls]);

    return (
        <>
            {headerComponent}
            <TabContainer>
                {/* @ts-ignore */}
                <Tabs id="verification-service-detail" fill={true} unmountOnExit={true}>
                    <Tab eventKey="general" title={validationErrors.general.tabTitle}>
                        <VerificationServiceDetailsGeneralTab
                            session={session}
                            axnDocumentationLinks={axnDocumentationLinks}
                            isDisabled={isUIDisabled}
                            validationErrors={validationErrors.general}
                            axnCompanies={axnCompanies}
                            axnRelyingParties={axnRelyingParties}
                            rpServiceConfigurationState={rpServiceConfigurationState}
                            initialRpServiceConfigurationWorkflowID={initialRpServiceConfiguration.workflowID}
                            setRpServiceConfigurationState={setRpServiceConfigurationState}
                            simpleAxnWorkflows={simpleAxnWorkflows}
                            simpleAxnAdminWorkflows={simpleAxnAdminWorkflows}
                            selectedLanguage={selectedLanguage}
                            availableLanguages={availableLanguages}
                            isMultiLanguageEnabled={isMultiLanguageEnabled}
                            selectedAttributes={selectedAttributes}
                            showPinCodeSection={showPinCodeSection}
                            showIncodeFlowTabs={showIncodeFlowTabs}
                            redirectUrlsInformation={redirectUrlsInformation}
                            handleGetRelyingPartyServicesRequest={handleGetRelyingPartyServicesRequest}
                            handleGetWorkflowAppliedProfilesRequest={handleGetWorkflowAppliedProfilesRequest}
                            handleCreateWorkflow={onCreateWorkflow}
                            updateProperties={updateProperties}
                        />
                    </Tab>
                    {initialRpServiceConfiguration.permissions.canViewServiceOfferings && (
                        <Tab eventKey="services" title={validationErrors.services.tabTitle}>
                            <VerificationServiceDetailsServicesTab
                                isDisabled={isUIDisabled || !initialRpServiceConfiguration.permissions.canModifyServiceOfferings}
                                validationErrors={validationErrors.services}
                                assertionAttributes={assertionAttributes}
                                serviceOfferingRefreshRateDefault={serviceOfferingRefreshRateDefault}
                                initialRpServiceConfiguration={initialRpServiceConfiguration}
                                assertionStatusMaps={assertionStatusMaps}
                                trustScoreServiceOfferingIDs={trustScoreServiceOfferingIDs}
                                assertionScoreServiceOfferingIDs={assertionScoreServiceOfferingIDs}
                                scoreServiceOfferingIDs={scoreServiceOfferingIDs}
                                rpServiceConfigurationState={rpServiceConfigurationState}
                                showIncodeFlowTabs={showIncodeFlowTabs}
                                setRpServiceConfigurationState={setRpServiceConfigurationState}
                            />
                        </Tab>
                    )}
                    {(
                        isUsingThemedUI &&
                        initialRpServiceConfiguration.permissions.canViewAXNUICustomization &&
                        initialRpServiceConfiguration.permissions.canViewBranding
                    ) && (
                            <Tab eventKey="theming" title={validationErrors.theming.tabTitle}>
                                <VerificationServiceDetailsThemingTab
                                    session={session}
                                    hasUnsubmittedChangeRequest={hasUnsubmittedChangeRequest}
                                    isDisabled={isUIDisabled}
                                    selectedLanguage={selectedLanguage}
                                    availableLanguages={availableLanguages}
                                    isMultiLanguageEnabled={isMultiLanguageEnabled}
                                    multiLanguageAXNUIContent={multiLanguageAXNUIContent}
                                    validationErrors={validationErrors.theming}
                                    hasDisplayableDocumentCaptureAttributes={hasDisplayableDocumentCaptureAttributes}
                                    showMessagingTab={showMessagingTab}
                                    showIncodeFlowTabs={showIncodeFlowTabs}
                                    availableMessagingTemplateTypes={availableMessagingTemplateTypes}
                                    messagingTemplateConfigurations={messagingTemplateConfigurations}
                                    selectedMessagingTemplates={selectedMessagingTemplates}
                                    axnCompanies={axnCompanies}
                                    axnRelyingParties={axnRelyingParties}
                                    showKbaFormTab={showKbaFormTab}
                                    rpServiceConfigurationState={rpServiceConfigurationState}
                                    emailTemplateFields={mergedEmailTemplateFields}
                                    axnuiContent={mergedAXNUIContent}
                                    axnuiLayout={mergedAXNUILayout}
                                    axnuiTheme={mergedAXNUITheme}
                                    inheritedEmailTemplateFields={inheritedEmailTemplateFields}
                                    inheritedAXNUIContent={inheritedAXNUIContent}
                                    inheritedAXNUILayout={inheritedAXNUILayout}
                                    inheritedAXNUITheme={inheritedAXNUITheme}
                                    isDisplayResultsEnabled={isDisplayResultsEnabled}
                                    inheritedIsDisplayResultsEnabled={inheritedIsDisplayResultsEnabled}
                                    messagingTemplateCache={messagingTemplateCache}
                                    emailFromAddress={emailFromAddress}
                                    customLanguages={customLanguages}
                                    redirectUrlsInformation={redirectUrlsInformation}
                                    axnAttributeTypeFields={axnAttributeTypeFields}
                                    axnAttributeTypeFieldCustomizations={axnAttributeTypeFieldCustomizations}
                                    setMessagingTemplateCache={setMessagingTemplateCache}
                                    setRpServiceConfigurationState={setRpServiceConfigurationState}
                                    setHasFetchedMessagingTemplates={setHasFetchedMessagingTemplates}
                                    uploadFileToS3={uploadFileToS3}
                                    updateProperties={updateProperties}
                                />
                            </Tab>
                        )}
                    {(
                        isUsingDCUIVersion2 &&
                        hasDisplayableDocumentCaptureAttributes &&
                        initialRpServiceConfiguration.permissions.canViewDCUICustomization &&
                        initialRpServiceConfiguration.permissions.canViewBranding
                    ) && (
                            <Tab eventKey="document-capture" title={validationErrors.dcuiTheming.tabTitle}>
                                <VerificationServiceDetailsDCUIThemingTab
                                    isDisabled={isUIDisabled}
                                    selectedLanguage={selectedLanguage}
                                    availableLanguages={availableLanguages}
                                    isMultiLanguageEnabled={isMultiLanguageEnabled}
                                    validationErrors={validationErrors.dcuiTheming}
                                    multiLanguageDCUIContent={multiLanguageDCUIContent}
                                    dcuiContent={mergedDCUIContent}
                                    dcuiTheme={mergedDCUITheme}
                                    inheritedDCUIContent={inheritedDCUIContent}
                                    inheritedDCUITheme={inheritedDCUITheme}
                                    documentCaptureSteps={documentCaptureSteps}
                                    activeAcuantProperties={activeAcuantProperties}
                                    rpServiceConfigurationState={rpServiceConfigurationState}
                                    setRpServiceConfigurationState={setRpServiceConfigurationState}
                                    uploadFileToS3={uploadFileToS3}
                                />
                            </Tab>
                        )}
                    <Tab eventKey="attributes" title={validationErrors.attributes.tabTitle}>
                        <VerificationServiceDetailsAttributesTabContainer
                            isDisabled={isUIDisabled}
                            selectedLanguage={selectedLanguage}
                            isMultiLanguageEnabled={isMultiLanguageEnabled}
                            availableLanguages={availableLanguages}
                            isUsingThemedUI={isUsingThemedUI}
                            axnuiContent={mergedAXNUIContent}
                            multiLanguageAXNUIContent={multiLanguageAXNUIContent}
                            validationErrors={validationErrors.attributes}
                            initialRpServiceConfiguration={initialRpServiceConfiguration}
                            rpServiceConfigurationState={rpServiceConfigurationState}
                            attributeLabels={attributeLabels}
                            serviceAttributeProperties={serviceAttributeProperties}
                            setRpServiceConfigurationState={setRpServiceConfigurationState}
                        />
                    </Tab>

                    {hasActiveServiceOfferings && (
                        <Tab eventKey="assertions" title={resources.pages.verificationServiceDetails.tabs.assertions.tabName}>
                            <VerificationServiceDetailsAssertionsTab
                                isDisabled={isUIDisabled}
                                rpServiceConfigurationState={rpServiceConfigurationState}
                                assertionStatusMaps={assertionStatusMaps}
                                assertionScoreServiceOfferingIDs={assertionScoreServiceOfferingIDs}
                                trustScoreServiceOfferingIDs={trustScoreServiceOfferingIDs}
                                assertionAttributes={assertionAttributes}
                                setRpServiceConfigurationState={setRpServiceConfigurationState}
                            />
                        </Tab>
                    )}
                    {policyType === PolicyTypes.TRUST_SCORE &&
                        (<Tab eventKey="scores" title={validationErrors.scores.tabTitle}>
                            <VerificationServiceDetailsScoresTab
                                isDisabled={isUIDisabled}
                                validationErrors={validationErrors.scores}
                                assertionStatusMaps={assertionStatusMaps}
                                scoreServiceOfferingIDs={scoreServiceOfferingIDs}
                                assertionScoreServiceOfferingIDs={assertionScoreServiceOfferingIDs}
                                trustScoreServiceOfferingIDs={trustScoreServiceOfferingIDs}
                                scoreReferenceData={scoreReferenceData}
                                rpServiceConfigurationState={rpServiceConfigurationState}
                                setRpServiceConfigurationState={setRpServiceConfigurationState} />
                        </Tab>)}
                    <Tab eventKey="policies" title={validationErrors.policies.tabTitle}>
                        <VerificationServiceDetailsPoliciesTabContainer
                            isDisabled={isUIDisabled}
                            isUsingThemedUI={isUsingThemedUI}
                            validationErrors={validationErrors.policies}
                            axnCompanies={axnCompanies}
                            axnRpServices={axnRpServices}
                            selectedLanguage={selectedLanguage}
                            axnuiContent={mergedAXNUIContent}
                            multiLanguageAXNUIContent={multiLanguageAXNUIContent}
                            availableLanguages={availableLanguages}
                            isMultiLanguageEnabled={isMultiLanguageEnabled}
                            axnAttributeTypes={axnAttributeTypes}
                            axnAcquiredAttributes={axnAcquiredAttributes}
                            axnAttributeTypeFields={axnAttributeTypeFields}
                            policyType={policyType}
                            initialPolicyType={initialPolicyType}
                            rpServiceConfigurationState={rpServiceConfigurationState}
                            simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                            axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                            rpServiceID={rpServiceConfigurationState.id}
                            workflowID={rpServiceConfigurationState.workflowID}
                            setRpServiceConfigurationState={setRpServiceConfigurationState}
                        />
                    </Tab>
                    {rpServiceConfigurationState.serviceType === OIDC_LOGIN_SERVICE_TYPE && (
                        <Tab eventKey="federated-idp" title={resources.pages.verificationServiceDetails.tabs.federatedIdp.tabName}>
                            <VerificationServiceDetailsFederatedIdpTab
                                isDisabled={isUIDisabled}
                                serviceOfferingRefreshRateDefault={serviceOfferingRefreshRateDefault}
                                serviceOfferingRefreshRates={serviceOfferingRefreshRates}
                                rpServiceConfigurationState={rpServiceConfigurationState}
                                setRpServiceConfigurationState={setRpServiceConfigurationState}
                            />
                        </Tab>
                    )}
                </Tabs>
            </TabContainer>
        </>
    );
};